import Cookies from "js-cookie";
import { repConnectionRequestsApi } from "@/Redux/apis/repConnectionRequests";
import { refreshAlphaMdMessages } from "@/Redux/slice/chatSlice";
import { ALPHAMD_SENDER_ID } from "@/services/alphaMdMessages";
import { io } from 'socket.io-client';
import { store } from '../Redux/store/store';
import {
    cancelMeeting,
    addMessage,
    setMessageStatus,
    setUserList,
    fetchMessages,
    setCurrentUserId,
    receiveMessage,
    markMessagesAsRead,
    confirmMeeting,
    confirmMeetingFromReps,
    scheduleRequestedMeetingWithReps,
    cancelRequestedMeetingWithReps
} from '../Redux/slice/chatSlice';
import {
    addNotification,
    fetchNotifications,
    markAsReadAndRemoveNotificationsBySenderAndUser,
    removeNotification
} from "../Redux/slice/notificationSlice";
import { showToast } from '@/Redux/toast-message/toast';
import { toast } from 'react-toastify';
import { endDisabledSession, registerDisabledSessionSocket } from "./disabledSession";

class SocketService {
    constructor() {
        this.socket = null;
        this.sessionToken = null;
        this.sessionUserId = null;
        this.sessionType = null;
    }
    handleRedirect = async (data, router) => {
        console.log(data, "data")
        console.log(router, "router")
        const state = store.getState();
        const userData = state.userData?.userData?.user; // Assuming 'userData' contains the user info you need


        if (userData && userData?.type !== 'reps') {
            router.push(`/provider/message?userId=${data?.senderId}`)
        }

        if (userData && userData?.type === 'reps') {
            router.push(`/reps/messages?userId=${data?.senderId}`)
        }

        // await store.dispatch(markNotificationAsRead({notificationId: data?._id}));
        await store.dispatch({ type: 'notifications/markAsRead', payload: { notificationIds: [data?._id] } });
    }

    connect(userId, router) {
        const token = Cookies.get("token");
        const type = store.getState().userData?.userData?.user?.type;
        if (this.socket && (this.sessionToken !== token || this.sessionUserId !== String(userId) || this.sessionType !== type)) {
            this.disconnectSocket();
        }
        if (!this.socket) {
            this.sessionToken = token;
            this.sessionUserId = String(userId);
            this.sessionType = type;
            this.socket = io(process.env.NEXT_PUBLIC_SOCKET_SERVER_URL, {
                auth: callback => {
                    this.sessionToken = Cookies.get("token");
                    callback({ token: this.sessionToken });
                },
                transports: ["websocket"],
                reconnection: true,
                reconnectionAttempts: Infinity, // keep trying to reconnect
                reconnectionDelay: 1000,
                // Without a cap the client retried every second forever. When
                // the socket host is unreachable (e.g. its TLS certificate is
                // rejected) that produced one console error per second.
                reconnectionDelayMax: 30000,
            });

            this.socket.on('connect', () => this.onConnect(userId));
            this.socket.on('reconnect', () => this.onConnect(userId)); // Reuse onConnect logic for reconnects
            this.socket.on('disconnect', (reason) => this.onDisconnect(reason));
            const sessionSocket = this.socket;
            sessionSocket.on('connect_error', error => {
                if (this.socket !== sessionSocket) return;
                if (error?.data?.code === 'ACCOUNT_DISABLED') {
                    const ended = endDisabledSession({ dispatch: store.dispatch, getState: store.getState,
                        token: this.sessionToken, id: this.sessionUserId, type: this.sessionType,
                        message: error.data.message || error.message });
                    if (!ended && this.socket === sessionSocket) this.disconnectSocket();
                    return;
                }
                this.onError('Connection Error', error);
            });
            this.socket.on('reconnect_error', (error) => this.onError('Reconnection Error', error));
            this.socket.on('reconnect_failed', () => this.onError('Reconnection Failed', null));

            // Set up core event listeners
            this.setupEventListeners(userId, router);
        }
    }

    onConnect(userId) {
        // A successful connect clears the suppression set, so a later outage
        // is still surfaced rather than silently swallowed.
        this.reportedErrors = new Set();
        console.log('Connected to WebSocket server', userId);
        this.socket.emit('userConnected', userId);
        const user = store.getState().userData?.userData?.user;
        if (user?.type !== "reps") store.dispatch(refreshAlphaMdMessages());
        store.dispatch(fetchNotifications({ userId, refresh: true }));
        store.dispatch(setCurrentUserId(userId));
        store.dispatch(fetchMessages({ userId, page: 1, limit: 10 }));
    }

    onDisconnect(reason) {
        console.log('Disconnected from WebSocket server:', reason);
        // Additional logic can be added here if necessary (e.g., notify user, retry logic)
    }

    onError(type, error) {
        // Reconnection is unbounded, so the same failure repeats indefinitely.
        // Report each distinct failure once instead of once per attempt, and
        // reset on a successful connect so a genuine later outage is reported.
        this.reportedErrors = this.reportedErrors || new Set();
        if (this.reportedErrors.has(type)) return;
        this.reportedErrors.add(type);
        // A warning, not an error: the socket is optional to page function and
        // reconnection is automatic. `xhr poll error` here usually means the
        // socket host is unreachable or its TLS certificate is rejected —
        // check NEXT_PUBLIC_SOCKET_SERVER_URL and that host's certificate.
        console.warn(
            `Socket ${type} for ${process.env.NEXT_PUBLIC_SOCKET_SERVER_URL} — retrying in the background; further occurrences suppressed until reconnect.`,
            error
        );
    }

    setupEventListeners(userId, router) {
        if (!this.socket) {
            console.error('Socket is not initialized.');
            return;
        }

        const events = new Set();
        const sessionSocket = this.socket;
        sessionSocket.on('accountDisabled', data => {
            if (this.socket !== sessionSocket || !data?.id || !["physician", "clinic", "reps"].includes(data.type)) return;
            endDisabledSession({ dispatch: store.dispatch, getState: store.getState, token: this.sessionToken,
                id: data.id, type: data.type, message: data.message });
        });
        this.socket.on('connectionEvent', item => {
            if (!item.eventId || events.has(item.eventId)) return;
            events.add(item.eventId);
            if (events.size > 1000) events.delete(events.values().next().value);
            const user = store.getState().userData?.userData?.user;
            if (item.eventType === 'conversation.read') {
                if (item.payload?.senderId && item.payload?.receiverId) {
                    store.dispatch(markMessagesAsRead(item.payload));
                    store.dispatch(fetchMessages({ userId, page: 1, limit: 10 }));
                }
                store.dispatch(fetchNotifications({ userId, refresh: true }));
            }
            if (item.eventType === 'notification.created') store.dispatch(addNotification(item.payload));
            if (item.eventType === 'message.created') store.dispatch(fetchMessages({ userId, page: 1, limit: 10 }));
            if (item.eventType === 'rep_request.updated') store.dispatch(repConnectionRequestsApi.util.invalidateTags(['RequestStatuses']));
            if (user?.type !== 'reps' && ['message.created', 'rep_request.updated', 'conversation.read'].includes(item.eventType)) store.dispatch(refreshAlphaMdMessages());
        });
        this.socket.on('newMessage', (data) => {
            if (String(data?.senderId || '').toLowerCase() === ALPHAMD_SENDER_ID || String(data?.receiverId || '').toLowerCase() === ALPHAMD_SENDER_ID || data?.kind === 'rep_connection') return;
            console.log("receiveMessage", data, userId)
            const known = store.getState().chat.messages.some(chat => chat.id === data.senderId);
            if (known) store.dispatch(receiveMessage(data));
            else store.dispatch(fetchMessages({ userId, page: 1, limit: 10 }));
        });

        this.socket.on('cancelMeetingMessage', (data) => {
            store.dispatch(cancelMeeting(data));
        });

        this.socket.on('confirmMeetingFromRepsMessage', (data) => {
            store.dispatch(confirmMeetingFromReps(data));
        });

        this.socket.on('scheduleRequestedMeetingMessage', (data) => {
            store.dispatch(scheduleRequestedMeetingWithReps(data));
        });

        this.socket.on('cancelRequestedMeetingMessage', (data) => {
            store.dispatch(cancelRequestedMeetingWithReps(data));
        });

        this.socket.on('confirmMeetingMessage', (data) => {
            store.dispatch(confirmMeeting(data));
        });

        this.socket.on('messageStatusUpdated', ({ messageId, status }) => {
            store.dispatch(setMessageStatus({ messageId, status }));
        });

        this.socket.on('chatOpened', (boundary) => {
            store.dispatch(markMessagesAsRead(boundary));
            store.dispatch(fetchMessages({ userId, page: 1, limit: 10 }));
            store.dispatch(fetchNotifications({ userId, refresh: true }));
        });

        this.socket.on('user list', (users) => {
            store.dispatch(setUserList(users));
        });

        this.socket.on('newNotification', (data) => {
            console.log("NOTIFICATION FROM SOCKET", data)
            const notificationSplit = data?.title?.split(':');
            const notification = notificationSplit[0];
            console.log("MESSAGE_notification SOCKET", data)
            store.dispatch(addNotification(data));
            // toast.info(notification + ': ' + data?.description,
            //     {
            //         onClick: () => this.handleRedirect(data, router)
            //     },
            // )
        });
    }

    sendMessage(msg) {
        if (this.socket) {
            this.socket.emit('sendMessage', msg, (response) => {
                if (response.success) {
                    // store.dispatch(addMessage(response?.data));
                }
            });
        } else {
            console.error('Socket is not connected.');
        }
    }

    scheduleRequestedMeeting(data) {
        if (this.socket) {
            this.socket.emit('scheduleRequestedMeeting', data, (response) => {
                if (response.success) {
                    console.log('====response', response)
                    store.dispatch(scheduleRequestedMeetingWithReps(response?.data));
                }
            });
        } else {
            console.error('Socket is not connected.');
        }
    }

    cancelRequestedMeeting(data) {
        if (this.socket) {
            this.socket.emit('cancelRequestedMeeting', data, (response) => {
                if (response.success) {
                    console.log('====response', response)
                    store.dispatch(cancelRequestedMeetingWithReps(response?.data));
                }
            });
        } else {
            console.error('Socket is not connected.');
        }
    }

    cancelMetingByUser(msg) {
        if (this.socket) {
            this.socket.emit('cancelMeeting', msg, (response) => {
                if (response.success) {
                    store.dispatch(cancelMeeting(response?.data));
                    store.dispatch(removeNotification({ notificationId: response?.data?.appointmentId, type: 'meeting' }));
                }
            });
        } else {
            console.error('Socket is not connected.');
        }
    }

    confirmMetingByReps(msg) {
        if (this.socket) {
            this.socket.emit('confirmMeetingFromReps', msg, (response) => {
                if (response.success) {
                    store.dispatch(confirmMeetingFromReps(response?.data));
                    store.dispatch(removeNotification({ notificationId: response?.data?.appointmentId, type: 'meeting' }));
                }
            });
        } else {
            console.error('Socket is not connected.');
        }
    }

    confirmMetingByUser(msg) {
        if (this.socket) {
            this.socket.emit('confirmMeeting', msg, (response) => {
                if (response.success) {
                    store.dispatch(confirmMeeting(response?.data));
                    store.dispatch(removeNotification({ notificationId: response?.data?.appointmentId, type: 'meeting' }));
                }
            });
        } else {
            console.error('Socket is not connected.');
        }
    }

    disconnectSocket() {
        if (this.socket) {
            console.log('Disconnecting socket');
            this.socket.disconnect();
            this.socket = null;
        }
        this.sessionToken = null;
        this.sessionUserId = null;
        this.sessionType = null;
    }

    getSocket() {
        return this.socket;
    }
}

const socketService = new SocketService();
registerDisabledSessionSocket(token => {
    if (socketService.sessionToken === token) socketService.disconnectSocket();
});
export default socketService;
